import { AimsLogo, Avatar, Dropdown } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { clearAuthAndRedirect } from '@aimsos/auth';
import { useTheme } from '@aimsos/theme';
import { ChevronDown, UserRound, History, LogOut, Sun, Moon, Languages } from 'lucide-react';

/**
 * Top bar for the Workspace Selector: brand on the left, Help + the signed-in
 * user's avatar menu on the right (Account settings, Activity log, Sign out).
 *
 * Uses `Dropdown` + `Dropdown.Item`, the same primitives the in-shell TopNav
 * account menu is built on, so item styling and close-on-outside-click come
 * from the DS rather than being hand-rolled here.
 */
function SelectorTopBar({ user }) {
  const { t, i18n } = useTranslation('core-shell');
  const { resolvedTheme, setTheme } = useTheme();
  // `theme` is the raw stored preference ('light' | 'dark' | 'system') and
  // defaults to 'system' — comparing against it directly left Light always
  // highlighted under the (default) system preference, even when the actual
  // applied theme was dark. `resolvedTheme` is the theme actually in effect.
  const isDark = resolvedTheme === 'dark';
  const email = user?.email ?? '';
  const name = user?.name || email || 'User';
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <header className="flex items-center justify-between px-6 py-4 lg:px-10">
      <AimsLogo size={26} withText textClassName="text-base text-content" />

      <div className="flex items-center gap-4">
        <a
          href="https://docs.aimsos.ai"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-content-muted transition-colors hover:text-content"
        >
          {t('workspaceSelector.help')}
        </a>

        <Dropdown
          alignMenu="right"
          width="18rem"
          menuMaxHeight="none"
          trigger={
            <button
              type="button"
              aria-label={t('workspaceSelector.userMenu')}
              className="flex items-center gap-2 rounded-full border border-edge bg-surface/60 py-1 pl-1 pr-2 outline-none transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              {/* Sizing overrides follow the repo's convention for `Avatar`:
                  it always applies one of its five `size` presets, and its
                  initials inherit body type, so `size="sm"` (16px) clipped
                  them. The accent carries a light/dark pair. */}
              <Avatar
                initials={initials}
                src={user?.picture}
                alt={name}
                className="!h-7 !w-7 !min-h-[1.75rem] !min-w-[1.75rem] !border-0 bg-brand-500/20 font-semibold text-brand-700 dark:text-brand-300 [&>span]:text-xs"
              />
              <span className="hidden max-w-[180px] truncate text-sm text-content-secondary sm:block">
                {email}
              </span>
              <ChevronDown size={14} className="text-content-faint" />
            </button>
          }
        >
          <div className="border-b border-edge px-4 py-3">
            <p className="truncate text-sm font-medium text-content">{name}</p>
            {email && <p className="truncate text-xs text-content-muted">{email}</p>}
          </div>
          {/* Account settings / Activity log destinations from a PRE-workspace
              context are an open product question (CT-4943 flags it rather than
              guessing): they may have to enter a workspace first, or land on a
              shell-level settings surface. Disabled — not silent no-ops — so
              the menu doesn't look like it swallowed the click. */}
          <Dropdown.Item icon={UserRound} disabled onClick={() => {}}>
            {t('workspaceSelector.accountSettings')}
          </Dropdown.Item>
          <Dropdown.Item icon={History} disabled onClick={() => {}}>
            {t('workspaceSelector.activityLog')}
          </Dropdown.Item>
          <Dropdown.Separator />
          <div className="px-4 py-2">
            <p className="mb-2 text-xs font-medium text-content-faint">
              {t('workspaceSelector.theme')}
            </p>
            <div className="grid grid-cols-2 gap-1 rounded-lg border border-edge p-1">
              <button
                type="button"
                onClick={() => setTheme('light')}
                aria-pressed={!isDark}
                className={`flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-colors ${
                  !isDark
                    ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
                    : 'text-content-muted hover:bg-surface-hover-subtle'
                }`}
              >
                <Sun size={14} /> {t('workspaceSelector.themeLight')}
              </button>
              <button
                type="button"
                onClick={() => setTheme('dark')}
                aria-pressed={isDark}
                className={`flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-colors ${
                  isDark
                    ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
                    : 'text-content-muted hover:bg-surface-hover-subtle'
                }`}
              >
                <Moon size={14} /> {t('workspaceSelector.themeDark')}
              </button>
            </div>
          </div>
          <Dropdown.Separator />
          <div className="px-4 py-2">
            <p className="mb-2 text-xs font-medium text-content-faint">
              {t('topNav.language')}
            </p>
            <div className="grid grid-cols-2 gap-1 rounded-lg border border-edge p-1">
              {[
                { code: 'en', label: t('topNav.langEn') },
                { code: 'es', label: t('topNav.langEs') },
              ].map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => { void i18n.changeLanguage(code); }}
                  aria-pressed={i18n.language === code}
                  className={`flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-colors ${
                    i18n.language === code
                      ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
                      : 'text-content-muted hover:bg-surface-hover-subtle'
                  }`}
                >
                  <Languages size={14} /> {label}
                </button>
              ))}
            </div>
          </div>
          <Dropdown.Separator />
          <Dropdown.Item icon={LogOut} variant="danger" onClick={() => clearAuthAndRedirect()}>
            {t('workspaceSelector.signOut')}
          </Dropdown.Item>
        </Dropdown>
      </div>
    </header>
  );
}

export default SelectorTopBar;
